/**
 * Fault Management Types & Utilities
 * 
 * Copyright 2024 iodyne.  All rights reserved.
 * iodyne Proprietary/Confidental: INTERNAL USE ONLY
 */

import { fm_msgs } from "@/lib/fm";
import type { ReactElement } from "react";
import { useTranslation } from '@/i18n/client_hook';
import { defaultNS } from '@/i18n/settings';
import { parseCompactISO8601 } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

const CONTENT_TYPE_KB = "application/vnd.iodyne.kb";
export const DEVICE_FRU = "device";

export type FaultEventContext = {[key:string] : string};

export interface FaultEventURL {
    url: string,
    content_type: string
}

export enum FaultMsgPriority {
    low = "low",
    medium = "med",
    high = "hi"
}

export interface FaultMessage {
    event: string,
    mguid: string,
    fguid: string,
    ts: string,
    visible: boolean,
    resolve: boolean,
    prio: FaultMsgPriority,
    fru?: string,
    pn?: string,
    sn?: string,
    data?: {}
}

export interface FaultEventType {
    event_type: string,
    info: { [key:string] : string },
    impact: { [key:string] : string },
    action: { [key:string] : string },
    urls: FaultEventURL[]
}

var faultsList:{[key:string] : FaultEventType} = {};
fm_msgs.forEach((item, index) => {
    faultsList[item.event_type] = (item as FaultEventType);
});

/**
 * Repackage fault log as a dictionary with faults organized by FRU.
 * @param faultLog 
 */
export function faultsByFRU(faultLog?:FaultMessage[]):{[key:string] : FaultMessage[]} {
    var fault_map:{[key:string] : FaultMessage[]} = {};

    faultLog && faultLog.forEach((fault:FaultMessage) => {
        const fru = fault.fru ? fault.fru : DEVICE_FRU;
        if(!(fru in fault_map)) {
            fault_map[fru] = [];
        }
        fault_map[fru].push(fault)
    });

    Object.keys(fault_map).forEach((fru:string) => {
        fault_map[fru].sort((a:FaultMessage,b:FaultMessage) => {
            const a_ts = parseCompactISO8601(a.ts);
            const b_ts = parseCompactISO8601(b.ts);
            if(+a_ts === +b.ts) { return 0; }
            else if ( a_ts > b_ts ) { return -1; }
            
            return 1;
        });
    });

    return fault_map;
}

/**
 * Replace variables in fault message string with values in `context`.
 * NOTES: Replacement variable format in `msg` is `${variable}`
 * @param msg 
 * @param context 
 */
function fmtFaultString(msg:string, context?:FaultEventContext) {
    var rendered_msg = msg;
    if(context) {
        Object.keys(context).forEach((key:string) => {
            rendered_msg = rendered_msg.replace("${"+key+"}", context[key])
        });
    }

    return rendered_msg;
}


/**
 * Return localized short description of given fault type.
 * @param eventType 
 * @param locale 
 * @param context - Optional dictionary of replacement variables for messages in the fault. 
 * @returns 
 */
export function shortDescForFault(eventType:string, locale?:string, context?:FaultEventContext):string {
    const fault = faultsList[eventType];
    const lang_code = locale ? locale : "en";

    return fmtFaultString(fault.info[lang_code], context)
}

/**
 * Return the primary knowledgebase article for the given fault type.
 * @param eventType 
 * @returns 
 */
export function kbArticleForFault(eventType:string):string | undefined {
    const fault = faultsList[eventType];
    const url:FaultEventURL = fault?.urls.filter((val) => { return val.content_type == CONTENT_TYPE_KB })[0]

    return url?.url;
}

/**
 * Format a {@link FaultEventType} into a localized HTML representation.
 * @param eventType  
 * @param locale 
 * @param context - Optional dictionary of replacement variables for messages in the fault. 
 * @returns {@link ReactElement}
 */
export function htmlForFault(eventType:string, locale?:string, context?:FaultEventContext):ReactElement {
    const { t } = useTranslation(defaultNS);
    const fault = faultsList[eventType];
    const lang_code = locale ? locale : "en";
    const url:FaultEventURL = fault?.urls.filter((val) => { return val.content_type == CONTENT_TYPE_KB })[0]
    
    return <div className="m-2 text-left flex flex-col max-w-[300px] p-2 text-sm">
        <div className="font-semibold">
            { t("label_description") }
        </div>
        <div>
            {fmtFaultString(fault.info[lang_code], context)}
        </div>
        <div>
            {fmtFaultString(fault.impact[lang_code], context)}
        </div>
        <div className="mt-3 font-semibold">
            { t("label_resolution") }
        </div>
        <div>
            {fmtFaultString(fault.action[lang_code], context)}
        </div>
        { url && <div className="mt-3">
            {fault.urls.length && <Link to={url.url} target="_blank" className="underline hover:opacity-85">{t("label_learn_more")}</Link>}
        </div> }
    </div>
}